import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Input } from "@/components/ui/inputForm";
import { Button } from "@/components/ui/button";
import { message, Select, ConfigProvider, theme as antdTheme, Checkbox } from "antd";
import { getServicoById, saveServico } from "@/services/servico";

export const tipos = [
  { value: "CABINEVIP", label: "Cabine" },
  { value: "ORIENTACAO", label: "Orientação" },
  { value: "MONITORIA", label: "Monitoria" },
  { value: "MATRICULA", label: "Matricula" },
  { value: "SIMULADO", label: "Simulado" },
  { value: "ARMARIO", label: "Armário" },
];

const turnosOptions = [
  { value: 'Manhã', label: 'Manhã' },
  { value: 'Tarde', label: 'Tarde' },
  { value: 'Noite', label: 'Noite' },
];

const defaultValues = {
  nome: "",
  valor: "",
  valorDesconto: "",
  tipo: "",
  possuiContrato: false,
  numeroCabines: "",
  turnos: [],
  ativo: true,
  defaultChecked: false,
};

function formatCurrency(value: string | number) {
  if (value === null || value === undefined || value === "") return "";
  const number = typeof value === "number" ? value : Number(value.toString().replace(/\D/g, "")) / 100;
  return number.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function parseCurrency(value: string) {
  if (!value) return 0;
  return Number(value.replace(/[^\d]/g, "")) / 100;
}

export default function CadastroServico() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues,
    mode: "onBlur",
  });

  const tipoSelecionado = watch("tipo");

  useEffect(() => {
    if (isEdit && id) {
      getServicoById(id).then((res) => {
        if (res.status === 200) {
          const servico = res.data;
          const shiftMap: { [key: string]: string } = { M: 'Manhã', T: 'Tarde', N: 'Noite' };
          const turnosArray = servico.turnos ? servico.turnos.split(',').map((t: string) => shiftMap[t]).filter(Boolean) : [];
          
          reset({
            ...defaultValues,
            ...servico,
            valor: formatCurrency(servico.valor),
            valorDesconto: formatCurrency(servico.valorDesconto),
            possuiContrato: !!servico.possuiContrato,
            numeroCabines: servico.numeroCabines ? String(servico.numeroCabines) : "",
            turnos: turnosArray,
            ativo: servico.ativo !== undefined ? servico.ativo : true,
            defaultChecked: !!servico.defaultChecked,
          });
        }
      });
    } else {
      reset(defaultValues);
    }
  }, [id, isEdit, reset]);

  const onSubmit = async (data: any) => {
    try {
      const turnoString = data.turnos?.map((t: string) => t.charAt(0)).join(',') || '';
      const payload = {
        ...data,
        id: id || undefined,
        valor: parseCurrency(data.valor),
        valorDesconto: parseCurrency(data.valorDesconto),
        numeroCabines: data.tipo === "0" ? Number(data.numeroCabines) : undefined,
        possuiContrato: !!data.possuiContrato,
        turnos: turnoString,
        defaultChecked: !!data.defaultChecked,
      };
      const response = await saveServico(payload);
      if (response.status === 200 || response.status === 201) {
        message.success("Serviço salvo com sucesso!");
        navigate("/servico");
      }
    } catch (error: any) {
      message.error("Erro ao salvar serviço");
    }
  };

  // Máscaras
  const handleValorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length < 3) value = value.padStart(3, "0");
    const number = Number(value) / 100;
    setValue("valor", number.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }), { shouldValidate: true });
  };
  const handleValorDescontoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length < 3) value = value.padStart(3, "0");
    const number = Number(value) / 100;
    setValue("valorDesconto", number.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }), { shouldValidate: true });
  };

  return (
    <div className="max-w-4xl mx-auto p-8">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-8">
          <span className="text-white text-2xl">{isEdit ? "Editar Serviço" : "Cadastro de Serviço"}</span>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div className="flex items-center mt-6 col-span-2">
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm] }}>
              <Checkbox
                id="ativo"
                checked={watch("ativo")}
                onChange={(e) => setValue("ativo", e.target.checked, { shouldValidate: true })}
              >
                <label htmlFor="ativo" className="text-white ml-2">Ativo</label>
              </Checkbox>
            </ConfigProvider>
          </div>
          <div className="col-span-2">
            <label>Nome *</label>
            <Input type="text" placeholder="Nome" {...register("nome", { required: "Nome é obrigatório" })} />
            {errors.nome && <span className="text-red-600">{errors.nome.message}</span>}
          </div>
          <div>
            <label>Valor *</label>
            <Input
              type="text"
              placeholder="R$ 0,00"
              {...register("valor", { required: "Valor é obrigatório" })}
              onChange={handleValorChange}
              value={watch("valor")}
            />
            {errors.valor && <span className="text-red-600">{errors.valor.message}</span>}
          </div>
          <div>
            <label>Valor Desconto *</label>
            <Input
              type="text"
              placeholder="R$ 0,00"
              {...register("valorDesconto", { required: "Valor desconto é obrigatório" })}
              onChange={handleValorDescontoChange}
              value={watch("valorDesconto")}
            />
            {errors.valorDesconto && <span className="text-red-600">{errors.valorDesconto.message}</span>}
          </div>
          <div className="col-span-2">
            <label>Tipo *</label>
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
              <Select
                className="w-full h-10"
                value={watch("tipo")}
                onChange={value => setValue("tipo", value, { shouldValidate: true })}
                options={tipos}
                placeholder="Selecione o tipo"
              />
            </ConfigProvider>
            {errors.tipo && <span className="text-red-600">Tipo é obrigatório</span>}
          </div>
          <div className="col-span-2">
            <label>Turnos</label>
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
              <Select
                mode="multiple"
                allowClear
                style={{ width: '100%' }}
                className="w-full h-10"
                placeholder="Selecione os turnos"
                value={watch("turnos")}
                onChange={value => setValue("turnos", value, { shouldValidate: true })}
                options={turnosOptions}
              />
            </ConfigProvider>
          </div>
          <div className="flex items-center mt-6 col-span-2">
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm] }}>
              <Checkbox
                id="possuiContrato"
                checked={watch("possuiContrato")}
                onChange={(e) => setValue("possuiContrato", e.target.checked, { shouldValidate: true })}
              >
                <label htmlFor="possuiContrato" className="text-white ml-2">Possui Contrato</label>
              </Checkbox>
            </ConfigProvider>
          </div>
          <div className="flex items-center mt-6 col-span-2">
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm] }}>
              <Checkbox
                id="defaultChecked"
                checked={watch("defaultChecked")}
                onChange={(e) => setValue("defaultChecked", e.target.checked, { shouldValidate: true })}
              >
                <label htmlFor="defaultChecked" className="text-white ml-2">Marcado Padrão</label>
              </Checkbox>
            </ConfigProvider>
          </div>
          {tipoSelecionado === "CABINEVIP" && (
            <div className="col-span-2">
              <label htmlFor="numeroCabines">Número de Cabines</label>
              <Input
                type="number"
                placeholder="Número de Cabines"
                {...register("numeroCabines", { required: tipoSelecionado === "CABINEVIP" ? "Número de cabines é obrigatório" : false })}
                value={watch("numeroCabines")}
                min={0}
              />
              {errors.numeroCabines && <span className="text-red-600">{errors.numeroCabines.message}</span>}
            </div>
          )}
          {tipoSelecionado === "ARMARIO" && (
            <div className="col-span-2">
              <label htmlFor="numeroCabines">Número de Armários</label>
              <Input
                type="number"
                placeholder="Número de Armários"
                {...register("numeroCabines", { required: tipoSelecionado === "ARMARIO" ? "Número de armários é obrigatório" : false })}
                value={watch("numeroCabines")}
                min={0}
              />
              {errors.numeroCabines && <span className="text-red-600">{errors.numeroCabines.message}</span>}
            </div>
          )}
        </div>
        <div className="flex justify-start p-2 mt-12 gap-8">
          <Button
            className="flex gap-2"
            variant="destructive"
            type="button"
            onClick={() => navigate("/servico")}
          >
            Cancelar
          </Button>
          <Button className="flex gap-2" type="submit">
            Salvar
          </Button>
        </div>
      </form>
    </div>
  );
} 